import { useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "../../components/LoadingIndicator";
import CommonButton from "../../components/CommonButton";
import CommonNavBar from "../../components/CommonNavBar";
import useThemeHook from "../../hooks/useThemeHook";
import { THEME_COLORS } from "../../themes/DarkLight";
import GlobalAlertCardBuilder from "../../utils/GlobalAlertCardBuilder";
import { CreatePayment } from "../../utils/PiIntegration";
import Text from "../../components/ThemedText";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import { FONTSIZE, imagesUrls } from "../../../constants";
import NumberSelector from "../../components/numberSelector/NumberSelector";
import PageView from "../../components/PageView";
import { useDispatch, useSelector } from "react-redux";
import emitter, { EmitterEvents } from "../../utils/eventsEmitter";
import arthereumProvider from "../../../backend/ArthereumProvider";
import getUserSignInData from "../../utils/getUserSignInData";
import { actions } from "../../redux";
import { CheckmarkDoneCircleOutline } from "react-ionicons";

function DepositPage() {
  const pi = [1, 10, 20, 100, 1000, 10000];
  const [isLoading, setIsLoading] = useState(false);
  const userData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;
  const theme: "light" | "dark" = useThemeHook();
  const dispatch = useDispatch();
  const depositSomePi = async (amount: number) => {
    try {
      if (Number(amount) === 0.0 || Number(amount) < 0) {
        GlobalAlertCardBuilder.connect().displayDefaultAlertCard(
          getTranslationText("amount"),
          "pending",
          theme,
          null
        );
        return;
      }
      if (isLoading) {
        return;
      }
      setIsLoading(true);

      await CreatePayment(user?.id, amount, "deposit", `Arthereum Wallet - Deposit ${amount} Pi`, async () => {
        // Update Redux locally instead of re-fetching from Firestore
        dispatch(actions.setActualUser({
          ...userData,
          user: {
            ...userData.user,
            picoinBalance: (userData.user.picoinBalance || 0) + amount,
          },
        }));
        emitter.emit(EmitterEvents.BOTTOM_SHEET_DISPLAY, {
          modal: {
            name: "alertModal",
            modalData: {
              description: getTranslationText("pi_deposit_success_desc"),
              title: getTranslationText("pi_deposit_success", { amount }),
              icon: (
                <CheckmarkDoneCircleOutline
                  color={THEME_COLORS[theme].piPurple}
                  height={"50px"}
                  width={"50px"}
                />
              ),
            },
          },
        });
      });
      setIsLoading(false);
    } catch (error) {
      alert(getTranslationText("error"));
    } finally {
      setIsLoading(false);
    }
  };
  const [amount, setAmount] = useState(0);
  const getTranslationText = useTranslationHook();
  return (
    <PageView>
      <CommonNavBar />
      <Text
        style={{
          padding: "10px",
          textAlign: "center",
          fontWeight: "500",
          fontSize: FONTSIZE.PRIMARY,
        }}
        text={getTranslationText("deposit")}
      />

      {/* Deposit & Multiply Promotion */}
      <div style={{ padding: "0 10px 16px 10px" }}>
        <div style={{
          background: "linear-gradient(135deg, #f59e0b22, #ea580c22)",
          borderRadius: "16px",
          padding: "16px",
          border: "1px solid #f59e0b44",
        }}>
          <p style={{
            fontWeight: "900",
            fontSize: "14px",
            color: THEME_COLORS[theme].inverse_theme,
            marginBottom: "12px",
            textAlign: "center",
          }}>
            🎁 Deposit & Multiply — Bonus Rewards!
          </p>
          {[
            { min: 100, ath: 500, extra: "" },
            { min: 500, ath: 3000, extra: " + Flash Boost" },
            { min: 1000, ath: 10000, extra: " + Turbo Charge + 5 Free Spins" },
          ].map((tier) => (
            <div key={tier.min} style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 12px",
              marginBottom: "6px",
              borderRadius: "12px",
              backgroundColor: THEME_COLORS[theme].component_background_color,
            }}>
              <span style={{
                fontSize: "12px",
                fontWeight: "700",
                color: THEME_COLORS[theme].inverse_theme,
              }}>
                Deposit {tier.min}+ π
              </span>
              <span style={{
                fontSize: "11px",
                fontWeight: "800",
                color: "#f59e0b",
              }}>
                +{tier.ath} ATH{tier.extra}
              </span>
            </div>
          ))}
          <p style={{
            fontSize: "10px",
            color: THEME_COLORS[theme].inverse_theme,
            opacity: 0.5,
            textAlign: "center",
            marginTop: "8px",
          }}>
            One-time bonus per tier · Deposit now to claim!
          </p>
        </div>
      </div>

      {pi.map((value, index) => {
        return (
          <div
            style={{ paddingBottom: "4px" }}
            onClick={async () => {
              await depositSomePi(value);
            }}
          >
            <div
              style={{
                padding: "10px",
                width: "100%",
                backgroundColor: THEME_COLORS[theme].component_background_color,
                height: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                borderRadius: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "10px",
                }}
              >
                <img
                  loading="lazy"
                  src={imagesUrls["pi"]}
                  alt=""
                  width="40px"
                  height="40px"
                  style={{ borderRadius: "8px" }}
                />
                <p
                  style={{
                    fontWeight: "500",
                    color: THEME_COLORS[theme].inverse_theme,
                  }}
                >
                  {value} Pi
                </p>
              </div>
            </div>
          </div>
        );
      })}
      <NumberSelector
        backgroundColor="white"
        title={"Pi"}
        interval={1}
        number={amount}
        padding={"10px"}
        setNumber={setAmount}
      />
      {isLoading && (
        <>
          <LoadingIndicator />
          <p style={{ color: THEME_COLORS[theme].orange }} className="padding">
            We are processing your payment please wait... Make sure to be on the
            pi browser. refresh the app after the payment completion.
          </p>
        </>
      )}
      <div className="padding">
        <CommonButton
          padding={null}
          text={"Deposit " + amount + " Pi"}
          white={null}
          color={null}
          onClickHandle={async () => await depositSomePi(amount)}
        />
      </div>
      <div style={{ minHeight: "40px", width: "100%" }}>
        <p></p>
      </div>
    </PageView>
  );
}

export default DepositPage;
